import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, Filter, BarChart3, Save, X, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { reportsService } from '../services/reportsService';
import { authService } from '../services/authService';
import { getDefaultDateRange } from '../utils/config';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Report Builder Page
 * Allows users to create custom reports
 */
const ReportBuilder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    report_name: '',
    account_id: '',
    start_date: '',
    end_date: '',
    metrics: [],
    breakdowns: [],
    format: 'both',
    filters: {
      app_key: [],
      ad_unit: [],
      country: [],
      ad_source: [],
    },
    schedule: {
      enabled: false,
      frequency: 'daily',
      time: '09:00',
    },
  });

  // Available options
  const availableMetrics = [
    { id: 'revenue', label: 'Revenue' },
    { id: 'impressions', label: 'Impressions' },
    { id: 'clicks', label: 'Clicks' },
    { id: 'ecpm', label: 'eCPM' },
    { id: 'ctr', label: 'CTR' },
    { id: 'fill_rate', label: 'Fill Rate' },
    { id: 'completion_rate', label: 'Completion Rate' },
    { id: 'active_users', label: 'Active Users' },
    { id: 'sessions', label: 'Sessions' },
  ];

  const availableBreakdowns = [
    { id: 'app', label: 'App' },
    { id: 'ad_unit', label: 'Ad Unit' },
    { id: 'country', label: 'Country' },
    { id: 'ad_source', label: 'Ad Source' },
  ];

  const formatOptions = [
    { value: 'table', label: 'Table Only' },
    { value: 'chart', label: 'Chart Only' },
    { value: 'both', label: 'Table & Chart' },
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  // Initialize date range (current month, 1st to today)
  useEffect(() => {
    const { startDate, endDate } = getDefaultDateRange();
    setFormData(prev => ({
      ...prev,
      start_date: startDate,
      end_date: endDate,
    }));
  }, []);

  // Fetch templates and accounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templatesRes, accountsRes] = await Promise.all([
          reportsService.getTemplates(),
          authService.getAccounts(),
        ]);

        setTemplates(templatesRes.data?.templates || []);
        setAccounts(accountsRes.data?.accounts || []);

        // Set default account if available
        if (accountsRes.data?.accounts?.length > 0) {
          const defaultAccount = accountsRes.data.accounts.find(acc => acc.is_default) || accountsRes.data.accounts[0];
          setFormData(prev => ({ ...prev, account_id: defaultAccount.id.toString() }));
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, []);

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      const config = template.default_config;

      setFormData(prev => ({
        ...prev,
        report_name: config.report_name || '',
        start_date: config.start_date || prev.start_date,
        end_date: config.end_date || prev.end_date,
        metrics: config.metrics || [],
        breakdowns: config.breakdowns || [],
        format: config.format || 'both',
        schedule: config.schedule || prev.schedule,
      }));
    }
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setFormData(prev => ({
      ...prev,
      start_date: range.startDate,
      end_date: range.endDate,
    }));
  };

  // Handle metric toggle
  const handleMetricToggle = (metricId, checked) => {
    setFormData(prev => ({
      ...prev,
      metrics: checked
        ? [...prev.metrics, metricId]
        : prev.metrics.filter(m => m !== metricId),
    }));
  };

  // Handle breakdown toggle
  const handleBreakdownToggle = (breakdownId, checked) => {
    setFormData(prev => ({
      ...prev,
      breakdowns: checked
        ? [...prev.breakdowns, breakdownId]
        : prev.breakdowns.filter(b => b !== breakdownId),
    }));
  };

  // Handle schedule field changes
  const handleScheduleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare report data
      const reportData = {
        report_name: formData.report_name,
        account_id: parseInt(formData.account_id),
        start_date: formData.start_date,
        end_date: formData.end_date,
        metrics: formData.metrics,
        breakdowns: formData.breakdowns,
        format: formData.format,
      };

      // Add filters if any are selected
      const hasFilters = Object.values(formData.filters).some(arr => arr.length > 0);
      if (hasFilters) {
        reportData.filters = formData.filters;
      }

      // Add schedule if enabled
      if (formData.schedule.enabled) {
        reportData.schedule = formData.schedule;
      }

      const response = await reportsService.create(reportData);

      setSuccess('Report created successfully!');

      // Redirect to reports list after 1 second
      setTimeout(() => {
        navigate('/reports');
      }, 1000);

    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to create report';
      setError(errorMessage);
      console.error('Report creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Report Builder</h1>
        <p className="text-muted-foreground text-xs">Create custom reports with your preferred metrics and breakdowns</p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-2 sm:mb-3 lg:mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-2 sm:mb-3 lg:mb-4 border-green-500 bg-green-500/10 dark:bg-green-500/20">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-600 dark:text-green-400">{success}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Template Selection */}
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Start from Template (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedTemplate?.id || ''} onValueChange={handleTemplateSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template to get started" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} - {template.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplate && (
              <p className="text-sm text-muted-foreground mt-2">
                Template: {selectedTemplate.name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="report_name">Report Name *</Label>
              <Input
                id="report_name"
                value={formData.report_name}
                onChange={(e) => handleFieldChange('report_name', e.target.value)}
                placeholder="e.g., Daily Revenue Report"
                required
              />
            </div>

            <div>
              <Label htmlFor="account_id">Account *</Label>
              <Select
                value={formData.account_id}
                onValueChange={(value) => handleFieldChange('account_id', value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(account => (
                    <SelectItem key={account.id} value={account.id.toString()}>
                      {account.account_name} {account.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date Range *</Label>
              <DateRangePicker
                startDate={formData.start_date}
                endDate={formData.end_date}
                onChange={handleDateRangeChange}
              />
            </div>
          </CardContent>
        </Card>

        {/* Metrics Selection */}
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Select Metrics *
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {availableMetrics.map(metric => (
                <div key={metric.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`metric-${metric.id}`}
                    checked={formData.metrics.includes(metric.id)}
                    onCheckedChange={(checked) => handleMetricToggle(metric.id, checked)}
                  />
                  <label
                    htmlFor={`metric-${metric.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {metric.label}
                  </label>
                </div>
              ))}
            </div>
            {formData.metrics.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">Please select at least one metric</p>
            )}
          </CardContent>
        </Card>

        {/* Breakdowns Selection */}
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Select Breakdowns (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {availableBreakdowns.map(breakdown => (
                <div key={breakdown.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`breakdown-${breakdown.id}`}
                    checked={formData.breakdowns.includes(breakdown.id)}
                    onCheckedChange={(checked) => handleBreakdownToggle(breakdown.id, checked)}
                  />
                  <label
                    htmlFor={`breakdown-${breakdown.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {breakdown.label}
                  </label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Format Selection */}
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader>
            <CardTitle>Report Format *</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={formData.format}
              onValueChange={(value) => handleFieldChange('format', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {formatOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Schedule Configuration */}
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Schedule (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="schedule_enabled"
                checked={formData.schedule.enabled}
                onCheckedChange={(checked) => handleScheduleChange('enabled', checked)}
              />
              <label
                htmlFor="schedule_enabled"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Enable automatic report generation
              </label>
            </div>

            {formData.schedule.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-muted">
                <div>
                  <Label htmlFor="schedule_frequency">Frequency *</Label>
                  <Select
                    value={formData.schedule.frequency}
                    onValueChange={(value) => handleScheduleChange('frequency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frequencyOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="schedule_time">Time (24-hour format) *</Label>
                  <Input
                    id="schedule_time"
                    type="time"
                    value={formData.schedule.time}
                    onChange={(e) => handleScheduleChange('time', e.target.value)}
                    required={formData.schedule.enabled}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Report will be generated at this time
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            type="submit"
            disabled={loading || !formData.report_name || !formData.account_id || formData.metrics.length === 0}
          >
            {loading ? 'Creating...' : 'Create Report'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/reports')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReportBuilder;
